import { ArrowLeft, Camera, CheckCircle2, Pencil, Plus, XCircle } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { useActionsHrms, useAgentsRh, useDossierHrms, useMonDossierHrms, useStructures } from '@/api/hooks';
import { LIBELLE_EVENEMENT_CONTRAT, LIBELLE_LIEN_CHARGE, LIBELLE_MODE_PAIEMENT_HRMS, LIBELLE_MOTIF_AFFECTATION, LIBELLE_SECTION_DOSSIER, LIBELLE_STATUT_CHARGE, LIBELLE_STATUT_CORRECTION, LIBELLE_STATUT_DOSSIER_HRMS, LIBELLE_STATUT_MARITAL, LIBELLE_STATUT_PAIEMENT_HRMS, LIBELLE_TYPE_CONTRAT, type CategorieAgent, type DossierHrms, type EvenementContratHrms, type LienChargeHrms, type ModePaiementHrms, type MotifAffectationHrms, type SectionDossierHrms, type StatutDossierHrms, type StatutMaritalHrms, type TypeContratHrms } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader, Vide } from '@/components/common';
import { AnomaliesDossier, PhotoAgent, StatutDossierHrmsBadge } from '@/components/hrms';
import { PiecesJointes } from '@/components/pieces-jointes';
import { RemunerationAgent } from '@/pages/paie/RemunerationAgent';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { dateFr, dateHeureFr } from '@/lib/utils';

const erreur = (e: unknown, defaut: string) => toast.error(e instanceof Error ? e.message : defaut);
const ONGLETS = ['identite', 'affectation', 'contrat', 'famille', 'social', 'paiement', 'remuneration', 'temps', 'corrections', 'pieces'] as const;
type Onglet = (typeof ONGLETS)[number];
const LIBELLE_ONGLET: Record<Onglet, string> = { identite: 'Identité', affectation: 'Affectations', contrat: 'Contrats', famille: 'Famille', social: 'Social', paiement: 'Paiement', remuneration: 'Rémunération', temps: 'Temps de travail', corrections: 'Corrections', pieces: 'Pièces' };
const CATEGORIES: CategorieAgent[] = ['PCA', 'DG', 'DGA', 'ADM', 'CAC', 'DIR', 'SDIR', 'CDIV', 'CSERV', 'CBUR', 'ACOL', 'AEXE'];
const Champ = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <span className="text-muted-foreground">{label}</span>
    <div>{children ?? '—'}</div>
  </div>
);
const ou = (v: string | null | undefined): string => (v && v.trim() ? v : '—');

/**
 * Dossier HRMS d'un agent (spécifications HRMS et Paie, phase 1, § 5.1 ; lot HP-1) : « Mon dossier » pour tout agent
 * (lecture, photo, demandes de correction, personnes à charge) et dossier tenu par les Ressources humaines (sections
 * modifiables, double contrôle des coordonnées de paiement, statut du dossier). Les droits viennent de l'API.
 */
export function DossierHrmsPage({ moi = false }: { moi?: boolean }) {
  const { id } = useParams();
  const session = useSession()!.utilisateur;
  const propre = useMonDossierHrms();
  const autre = useDossierHrms(moi ? undefined : id);
  const q = moi ? propre : autre;
  const [params, setParams] = useSearchParams();
  const onglet: Onglet = ONGLETS.includes(params.get('onglet') as Onglet) ? (params.get('onglet') as Onglet) : 'identite';
  const actions = useActionsHrms();
  const [dialogue, setDialogue] = useState<null | 'identite' | 'affectation' | 'contrat' | 'anciennete' | 'social' | 'temps' | 'charge' | 'paiement' | 'correction' | 'statut'>(null);
  const champPhoto = useRef<HTMLInputElement | null>(null);
  if (q.isLoading) return <Chargement />;
  if (q.error || !q.data) return <ErreurBloc erreur={q.error} />;
  const d = q.data;
  const f = d.fiche;
  const agentId = f.id;
  const estMoi = session.agentId === agentId;
  const gestion = session.profils.some((p) => p === 'DIRECTEUR_RH' || p === 'RESSOURCES_HUMAINES' || p === 'ADMINISTRATEUR' || p === 'DIRECTEUR_GENERAL');
  const changerOnglet = (o: string) => {
    const n = new URLSearchParams(params);
    n.set('onglet', o);
    setParams(n, { replace: true });
  };
  const deposerPhoto = async (fichier: File | undefined) => {
    if (!fichier) return;
    try {
      await actions.deposerPhoto.mutateAsync({ id: agentId, fichier });
      toast.success(d.droits.validerPhoto ? 'Photo enregistrée.' : 'Photo déposée : elle sera validée par les Ressources humaines.');
    } catch (e) {
      erreur(e, 'Photo refusée.');
    } finally {
      if (champPhoto.current) champPhoto.current.value = '';
    }
  };
  const validerPhoto = async () => {
    try {
      await actions.validerPhoto.mutateAsync(agentId);
      toast.success('Photo validée.');
    } catch (e) {
      erreur(e, 'Validation impossible.');
    }
  };
  const ongletsVisibles = ONGLETS.filter((o) => (o === 'famille' || o === 'social' || o === 'paiement' || o === 'remuneration' ? d.droits.voirConfidentiel : o === 'corrections' ? estMoi || d.droits.traiterCorrection : o === 'pieces' ? estMoi || d.droits.modifier : true));
  return (
    <div className="space-y-6">
      <PageHeader
        retour={
          <Link to={moi ? '/rh' : gestion ? '/rh/hrms' : `/rh/agents/${agentId}`} className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> {moi ? 'Mes démarches RH' : gestion ? 'Référentiel HRMS' : 'Fiche RH'}
          </Link>
        }
        titre={
          <span className="flex flex-wrap items-center gap-3">
            <PhotoAgent agentId={agentId} version={d.dossier.photo?.pieceId} nom={f.nom} prenom={f.prenom} statut={d.dossier.photo?.statut} />
            <span>
              {moi ? 'Mon dossier HRMS' : `${f.prenom} ${f.nom}`} <StatutDossierHrmsBadge statut={d.dossier.statut} />
            </span>
          </span>
        }
        sousTitre={`${f.fonction} · ${f.structure?.libelle ?? '—'} · ${f.matricule}${f.grade ? ` · ${f.grade}` : ''}`}
        actions={
          <span className="flex flex-wrap gap-2">
            {d.droits.deposerPhoto && (
              <>
                <input ref={champPhoto} type="file" accept="image/png,image/jpeg" className="hidden" data-testid="champ-photo" onChange={(e) => void deposerPhoto(e.target.files?.[0])} />
                <Button variant="outline" onClick={() => champPhoto.current?.click()} disabled={actions.deposerPhoto.isPending}>
                  <Camera className="size-4" /> {d.dossier.photo ? 'Remplacer la photo' : 'Déposer une photo'}
                </Button>
              </>
            )}
            {d.droits.validerPhoto && d.dossier.photo?.statut === 'A_VALIDER' && (
              <Button variant="outline" onClick={validerPhoto} disabled={actions.validerPhoto.isPending}>
                <CheckCircle2 className="size-4" /> Valider la photo
              </Button>
            )}
            {d.droits.demanderCorrection && (
              <Button onClick={() => setDialogue('correction')}>
                <Pencil className="size-4" /> Demander une correction
              </Button>
            )}
            {d.droits.controler && (
              <Button variant="outline" onClick={() => setDialogue('statut')}>
                Statut du dossier
              </Button>
            )}
            {!moi && (
              <Button asChild variant="ghost">
                <Link to={`/rh/agents/${agentId}`}>Fiche RH</Link>
              </Button>
            )}
          </span>
        }
      />

      <Card data-testid="dossier-synthese">
        <CardHeader>
          <CardTitle>Synthèse du dossier</CardTitle>
          <CardDescription>
            {d.dossier.libelleStatut}
            {d.dossier.controle ? ` · ${d.dossier.statut === 'VALIDE' ? 'validé' : 'contrôlé'} par ${d.dossier.controle.par ?? '—'} le ${dateHeureFr(d.dossier.controle.le)}` : ''}
            {d.dossier.importeLe ? ` · importé le ${dateHeureFr(d.dossier.importeLe)}${d.dossier.sourceImport ? ` (${d.dossier.sourceImport})` : ''}` : ''}
            {moi ? ' · Signalez toute erreur par une demande de correction : les Ressources humaines tiennent le dossier.' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AnomaliesDossier anomalies={d.dossier.anomalies} />
        </CardContent>
      </Card>

      <Tabs value={onglet} onValueChange={changerOnglet}>
        <TabsList className="flex-wrap">
          {ongletsVisibles.map((o) => (
            <TabsTrigger key={o} value={o}>
              {LIBELLE_ONGLET[o]}
              {o === 'corrections' && d.corrections.some((c) => c.statut === 'OUVERTE') && <Badge variant="secondary" className="ml-1">{d.corrections.filter((c) => c.statut === 'OUVERTE').length}</Badge>}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {onglet === 'identite' && (
        <Card data-testid="dossier-identite">
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center justify-between gap-2">
              Identité et coordonnées
              {d.droits.modifier && (
                <Button size="sm" variant="outline" onClick={() => setDialogue('identite')}>
                  <Pencil className="size-4" /> Modifier
                </Button>
              )}
            </CardTitle>
            <CardDescription>État civil, coordonnées, lieux de recrutement et de prestation, pièces administratives.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <Champ label="Nom et post-nom">{d.dossier.identite.nom}</Champ>
            <Champ label="Prénom">{ou(d.dossier.identite.prenom)}</Champ>
            <Champ label="Sexe">{d.dossier.identite.sexe === 'F' ? 'Féminin' : d.dossier.identite.sexe === 'M' ? 'Masculin' : '—'}</Champ>
            <Champ label="Date de naissance">{d.dossier.identite.dateNaissance ? dateFr(d.dossier.identite.dateNaissance) : '—'}</Champ>
            <Champ label="Lieu de naissance">{ou(d.dossier.identite.lieuNaissance)}</Champ>
            <Champ label="Statut marital">{d.dossier.identite.statutMarital ? LIBELLE_STATUT_MARITAL[d.dossier.identite.statutMarital] : '—'}</Champ>
            <Champ label="Téléphone">{ou(d.dossier.identite.telephone)}</Champ>
            <Champ label="Courriel">{d.dossier.identite.email}</Champ>
            <Champ label="Adresse">{ou(d.dossier.identite.adresse)}</Champ>
            <Champ label="Lieu de recrutement">{ou(d.dossier.identite.lieuRecrutement)}</Champ>
            <Champ label="Lieu de prestation">{ou(d.dossier.identite.lieuPrestation)}</Champ>
            <Champ label="Région">{ou(d.dossier.identite.region)}{d.dossier.identite.mutation ? ' · en mutation' : ''}</Champ>
            <Champ label="Date de retraite (calculée)">{d.dossier.identite.dateRetraite ? dateFr(d.dossier.identite.dateRetraite) : '—'}</Champ>
            <div className="sm:col-span-2 lg:col-span-3">
              <span className="text-muted-foreground">Pièces administratives</span>
              {d.dossier.identite.piecesAdministratives.length ? (
                <ul className="list-inside list-disc">
                  {d.dossier.identite.piecesAdministratives.map((p, i) => (
                    <li key={i}>
                      {p.type} n° {p.numero}
                      {p.validite ? ` · valide jusqu'au ${dateFr(p.validite)}` : ''}
                    </li>
                  ))}
                </ul>
              ) : (
                <div>—</div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {onglet === 'affectation' && (
        <Card data-testid="dossier-affectations">
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center justify-between gap-2">
              Affectations à effet daté
              <span className="flex gap-2">
                {d.droits.modifier && (
                  <Button size="sm" variant="outline" onClick={() => setDialogue('anciennete')}>
                    <Pencil className="size-4" /> Ancienneté
                  </Button>
                )}
                {d.droits.modifier && (
                  <Button size="sm" onClick={() => setDialogue('affectation')}>
                    <Plus className="size-4" /> Nouvelle affectation
                  </Button>
                )}
              </span>
            </CardTitle>
            <CardDescription>
              Poste, grade, catégorie, structure et supérieur hiérarchique ; l'affectation en cours alimente le référentiel. Engagement le {f.dateEngagement ? dateFr(f.dateEngagement) : '—'}
              {f.dateAnciennete ? `, ancienneté reconnue depuis le ${dateFr(f.dateAnciennete)}` : ''}
              {f.anciennete ? ` · ${f.anciennete.annees} an(s) et ${f.anciennete.mois} mois` : ''}.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!d.affectations.length ? (
              <Vide message="Aucune affectation enregistrée." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Période</TableHead>
                    <TableHead>Poste et grade</TableHead>
                    <TableHead>Structure</TableHead>
                    <TableHead className="hidden md:table-cell">Supérieur</TableHead>
                    <TableHead className="hidden lg:table-cell">Motif</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.affectations.map((a) => (
                    <TableRow key={a.id} className={a.effetAu === null ? 'font-medium' : 'text-muted-foreground'}>
                      <TableCell>
                        {dateFr(a.effetDu)} → {a.effetAu ? dateFr(a.effetAu) : 'en cours'}
                      </TableCell>
                      <TableCell>
                        {a.poste}
                        <div className="text-xs text-muted-foreground">
                          {a.grade ?? '—'} · {a.categorie}
                        </div>
                      </TableCell>
                      <TableCell>{a.structure ? `${a.structure.code} · ${a.structure.libelle}` : '—'}{a.lieuPrestation ? ` · ${a.lieuPrestation}` : ''}</TableCell>
                      <TableCell className="hidden md:table-cell">{a.superieur?.nom ?? '—'}</TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {LIBELLE_MOTIF_AFFECTATION[a.motif]}
                        {a.commentaire ? <div className="text-xs">{a.commentaire}</div> : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {onglet === 'contrat' && (
        <Card data-testid="dossier-contrats">
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center justify-between gap-2">
              Événements contractuels
              {d.droits.modifier && (
                <Button size="sm" onClick={() => setDialogue('contrat')}>
                  <Plus className="size-4" /> Nouvel événement
                </Button>
              )}
            </CardTitle>
            <CardDescription>Entrée, avenants, renouvellements, suspensions, reprises et fin de la relation contractuelle.</CardDescription>
          </CardHeader>
          <CardContent>
            {!d.contrats.length ? (
              <Vide message="Aucun événement contractuel." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Événement</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Début</TableHead>
                    <TableHead>Fin</TableHead>
                    <TableHead className="hidden md:table-cell">Référence</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.contrats.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>{LIBELLE_EVENEMENT_CONTRAT[c.evenement]}</TableCell>
                      <TableCell>{LIBELLE_TYPE_CONTRAT[c.type]}</TableCell>
                      <TableCell>{dateFr(c.debut)}</TableCell>
                      <TableCell>{c.fin ? dateFr(c.fin) : 'indéterminée'}</TableCell>
                      <TableCell className="hidden md:table-cell">
                        {c.reference ?? '—'}
                        {c.commentaire ? <div className="text-xs text-muted-foreground">{c.commentaire}</div> : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {onglet === 'famille' && d.personnesACharge && (
        <Card data-testid="dossier-famille">
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center justify-between gap-2">
              Situation familiale
              {d.droits.declarerCharge && (
                <Button size="sm" onClick={() => setDialogue('charge')}>
                  <Plus className="size-4" /> Déclarer une personne à charge
                </Button>
              )}
            </CardTitle>
            <CardDescription>
              Personnes à charge validées sur justificatif (acte de mariage, acte de naissance) par les Ressources humaines.
              {d.dossier.famille ? ` Fichier HRMS : ${d.dossier.famille.conjointsDeclares} conjoint(s) et ${d.dossier.famille.enfantsDeclares} enfant(s) déclarés.` : ''}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!d.personnesACharge.length ? (
              <Vide message="Aucune personne à charge déclarée." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Personne</TableHead>
                    <TableHead>Lien</TableHead>
                    <TableHead>Naissance</TableHead>
                    <TableHead>Statut</TableHead>
                    {d.droits.validerCharge && <TableHead />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.personnesACharge.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        {c.prenom ? `${c.prenom} ` : ''}
                        {c.nom}
                        {!c.pieceId && <div className="text-xs text-amber-700">sans justificatif</div>}
                      </TableCell>
                      <TableCell>{LIBELLE_LIEN_CHARGE[c.lien]}</TableCell>
                      <TableCell>{c.dateNaissance ? dateFr(c.dateNaissance) : '—'}</TableCell>
                      <TableCell>
                        <Badge variant={c.statut === 'VALIDEE' ? 'default' : c.statut === 'REFUSEE' ? 'destructive' : 'secondary'}>{LIBELLE_STATUT_CHARGE[c.statut]}</Badge>
                        {c.statut === 'VALIDEE' && c.debut ? <div className="text-xs text-muted-foreground">depuis le {dateFr(c.debut)}</div> : null}
                        {c.motifDecision ? <div className="text-xs text-muted-foreground">{c.motifDecision}</div> : null}
                      </TableCell>
                      {d.droits.validerCharge && (
                        <TableCell className="text-right">
                          {c.statut === 'DECLAREE' && <DecisionCharge agentId={agentId} chargeId={c.id} avecPiece={Boolean(c.pieceId)} />}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {onglet === 'social' && d.dossier.social && (
        <Card data-testid="dossier-social">
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center justify-between gap-2">
              Situation fiscale et sociale
              {d.droits.modifier && (
                <Button size="sm" variant="outline" onClick={() => setDialogue('social')}>
                  <Pencil className="size-4" /> Modifier
                </Button>
              )}
            </CardTitle>
            <CardDescription>Numéros CNSS et fiscal, régime applicable, date d'affiliation : réservés à l'agent, aux RH, à la paie et à la Direction financière.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
            <Champ label="Numéro CNSS">{ou(d.dossier.social.numeroCnss)}</Champ>
            <Champ label="Numéro fiscal">{ou(d.dossier.social.numeroFiscal)}</Champ>
            <Champ label="Régime social">{ou(d.dossier.social.regimeSocial)}</Champ>
            <Champ label="Affiliation CNSS">{d.dossier.social.dateAffiliationCnss ? dateFr(d.dossier.social.dateAffiliationCnss) : '—'}</Champ>
          </CardContent>
        </Card>
      )}

      {onglet === 'paiement' && d.paiements && (
        <Card data-testid="dossier-paiement">
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center justify-between gap-2">
              Coordonnées de paiement
              {d.droits.proposerPaiement && (
                <Button size="sm" onClick={() => setDialogue('paiement')}>
                  <Plus className="size-4" /> Nouvelles coordonnées
                </Button>
              )}
            </CardTitle>
            <CardDescription>Double contrôle (décision D24) : saisies par les Ressources humaines, validées par le Directeur RH ; l'agent est notifié à chaque validation. Une seule coordonnée valide à la fois.</CardDescription>
          </CardHeader>
          <CardContent>
            {!d.paiements.length ? (
              <Vide message="Aucune coordonnée de paiement enregistrée." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mode</TableHead>
                    <TableHead>Banque ou opérateur</TableHead>
                    <TableHead>Compte</TableHead>
                    <TableHead>Statut, saisie et validation</TableHead>
                    {d.droits.validerPaiement && <TableHead />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.paiements.map((p) => (
                    <TableRow key={p.id} className={p.statut === 'VALIDE' ? 'font-medium' : p.statut === 'PROPOSE' ? '' : 'text-muted-foreground'}>
                      <TableCell>{LIBELLE_MODE_PAIEMENT_HRMS[p.mode]} · {p.devise}</TableCell>
                      <TableCell>{p.banque ?? '—'}</TableCell>
                      <TableCell>{p.compte ?? '—'}</TableCell>
                      <TableCell>
                        <Badge variant={p.statut === 'VALIDE' ? 'default' : p.statut === 'PROPOSE' ? 'secondary' : p.statut === 'REFUSE' ? 'destructive' : 'outline'}>{LIBELLE_STATUT_PAIEMENT_HRMS[p.statut]}</Badge>
                        {p.motifRefus ? <div className="text-xs">{p.motifRefus}</div> : null}
                        <div className="text-xs text-muted-foreground">
                          saisies par {p.saisiPar ?? '—'} le {dateHeureFr(p.createdAt)}
                          {p.validePar ? ` · ${p.statut === 'REFUSE' ? 'refusées' : 'validées'} par ${p.validePar} le ${dateHeureFr(p.valideLe)}` : ''}
                        </div>
                      </TableCell>
                      {d.droits.validerPaiement && <TableCell className="text-right">{p.statut === 'PROPOSE' && <DecisionPaiement agentId={agentId} paiementId={p.id} />}</TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {onglet === 'remuneration' && d.droits.voirConfidentiel && <RemunerationAgent agentId={agentId} moi={estMoi} />}

      {onglet === 'temps' && (
        <Card data-testid="dossier-temps">
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center justify-between gap-2">
              Temps de travail
              {d.droits.modifier && (
                <Button size="sm" variant="outline" onClick={() => setDialogue('temps')}>
                  <Pencil className="size-4" /> Modifier
                </Button>
              )}
            </CardTitle>
            <CardDescription>Horaire de référence, calendrier, mode de pointage, éligibilité aux heures supplémentaires et à l'astreinte (préparent le module de la paie).</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-5">
            <Champ label="Horaire">{ou(d.dossier.tempsTravail.horaireCode)}</Champ>
            <Champ label="Calendrier">{ou(d.dossier.tempsTravail.calendrierCode)}</Champ>
            <Champ label="Pointage">{d.dossier.tempsTravail.modePointage === 'BADGE' ? 'Badge' : d.dossier.tempsTravail.modePointage === 'FICHE' ? 'Fiche de présence' : '—'}</Champ>
            <Champ label="Heures supplémentaires">{d.dossier.tempsTravail.eligibleHeuresSup ? 'Éligible' : 'Non éligible'}</Champ>
            <Champ label="Astreinte">{d.dossier.tempsTravail.eligibleAstreinte ? 'Éligible' : 'Non éligible'}</Champ>
          </CardContent>
        </Card>
      )}

      {onglet === 'corrections' && (
        <Card data-testid="dossier-corrections">
          <CardHeader>
            <CardTitle className="flex flex-wrap items-center justify-between gap-2">
              Demandes de correction
              {d.droits.demanderCorrection && (
                <Button size="sm" onClick={() => setDialogue('correction')}>
                  <Plus className="size-4" /> Nouvelle demande
                </Button>
              )}
            </CardTitle>
            <CardDescription>L'agent signale une information erronée ; les Ressources humaines acceptent (et portent la correction au dossier) ou refusent avec une réponse.</CardDescription>
          </CardHeader>
          <CardContent>
            {!d.corrections.length ? (
              <Vide message="Aucune demande de correction." />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Information</TableHead>
                    <TableHead>Valeur proposée</TableHead>
                    <TableHead>Statut</TableHead>
                    {d.droits.traiterCorrection && <TableHead />}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {d.corrections.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>{dateHeureFr(c.createdAt)}</TableCell>
                      <TableCell>
                        <div className="text-xs text-muted-foreground">{c.libelleSection}</div>
                        {c.champ}
                        {c.valeurActuelle ? <div className="text-xs text-muted-foreground">actuellement : {c.valeurActuelle}</div> : null}
                      </TableCell>
                      <TableCell>
                        {c.valeurProposee}
                        {c.motif ? <div className="text-xs text-muted-foreground">{c.motif}</div> : null}
                      </TableCell>
                      <TableCell>
                        <Badge variant={c.statut === 'ACCEPTEE' ? 'default' : c.statut === 'REFUSEE' ? 'destructive' : 'secondary'}>{LIBELLE_STATUT_CORRECTION[c.statut]}</Badge>
                        {c.reponse ? <div className="text-xs text-muted-foreground">{c.reponse}</div> : null}
                        {c.traitePar ? <div className="text-xs text-muted-foreground">par {c.traitePar} le {dateHeureFr(c.traiteLe)}</div> : null}
                      </TableCell>
                      {d.droits.traiterCorrection && <TableCell className="text-right">{c.statut === 'OUVERTE' && <ReponseCorrection agentId={agentId} correctionId={c.id} />}</TableCell>}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {onglet === 'pieces' && <PiecesJointes objetType="AGENT" objetId={agentId} typesSuggeres={['PIECE_IDENTITE', 'ACTE_ETAT_CIVIL', 'JUSTIFICATIF', 'CONTRAT', 'AUTRE']} titre="Pièces du dossier" description="Pièces d'identité, actes d'état civil et justificatifs : accès réservé à l'agent et aux Ressources humaines." />}

      {dialogue === 'identite' && <IdentiteDialog d={d} onClose={() => setDialogue(null)} />}
      {dialogue === 'affectation' && <AffectationDialog d={d} onClose={() => setDialogue(null)} />}
      {dialogue === 'contrat' && <ContratDialog d={d} onClose={() => setDialogue(null)} />}
      {dialogue === 'anciennete' && <AncienneteDialog d={d} onClose={() => setDialogue(null)} />}
      {dialogue === 'social' && <SocialDialog d={d} onClose={() => setDialogue(null)} />}
      {dialogue === 'temps' && <TempsDialog d={d} onClose={() => setDialogue(null)} />}
      {dialogue === 'charge' && <ChargeDialog d={d} onClose={() => setDialogue(null)} onPieces={() => { setDialogue(null); changerOnglet('pieces'); }} />}
      {dialogue === 'paiement' && <PaiementDialog d={d} onClose={() => setDialogue(null)} />}
      {dialogue === 'correction' && <CorrectionDialog d={d} onClose={() => { setDialogue(null); changerOnglet('corrections'); }} />}
      {dialogue === 'statut' && <StatutDialog d={d} onClose={() => setDialogue(null)} />}
      {moi && !session.profils.length && <p className="text-sm text-muted-foreground">Votre dossier est tenu par les Ressources humaines : vous le consultez, déposez votre photo et demandez les corrections utiles.</p>}
    </div>
  );
}

// ------------------------------------------------------------------ décisions en ligne
function DecisionCharge({ agentId, chargeId, avecPiece }: { agentId: string; chargeId: string; avecPiece: boolean }) {
  const actions = useActionsHrms();
  const [refus, setRefus] = useState(false);
  const [motif, setMotif] = useState('');
  const decider = async (decision: 'VALIDER' | 'REFUSER') => {
    try {
      await actions.deciderCharge.mutateAsync({ id: agentId, chargeId, corps: { decision, motif: motif || undefined } });
      toast.success(decision === 'VALIDER' ? 'Personne à charge validée.' : 'Déclaration refusée.');
      setRefus(false);
    } catch (e) {
      erreur(e, 'Décision impossible.');
    }
  };
  return (
    <span className="inline-flex flex-wrap items-center justify-end gap-1">
      <Button size="sm" variant="outline" disabled={!avecPiece || actions.deciderCharge.isPending} title={avecPiece ? undefined : 'Justificatif requis'} onClick={() => decider('VALIDER')}>
        <CheckCircle2 className="size-4" /> Valider
      </Button>
      {refus ? (
        <>
          <Input className="h-8 w-40" placeholder="Motif du refus" value={motif} onChange={(e) => setMotif(e.target.value)} />
          <Button size="sm" variant="destructive" disabled={!motif.trim() || actions.deciderCharge.isPending} onClick={() => decider('REFUSER')}>
            Confirmer
          </Button>
        </>
      ) : (
        <Button size="sm" variant="ghost" onClick={() => setRefus(true)}>
          <XCircle className="size-4" /> Refuser
        </Button>
      )}
    </span>
  );
}

function DecisionPaiement({ agentId, paiementId }: { agentId: string; paiementId: string }) {
  const actions = useActionsHrms();
  const [refus, setRefus] = useState(false);
  const [motif, setMotif] = useState('');
  const decider = async (decision: 'VALIDER' | 'REFUSER') => {
    try {
      await actions.deciderPaiement.mutateAsync({ id: agentId, paiementId, corps: { decision, motif: motif || undefined } });
      toast.success(decision === 'VALIDER' ? 'Coordonnées validées : l’agent est notifié.' : 'Coordonnées refusées.');
      setRefus(false);
    } catch (e) {
      erreur(e, 'Décision impossible.');
    }
  };
  return (
    <span className="inline-flex flex-wrap items-center justify-end gap-1">
      <Button size="sm" disabled={actions.deciderPaiement.isPending} onClick={() => decider('VALIDER')} data-testid="valider-paiement">
        <CheckCircle2 className="size-4" /> Valider
      </Button>
      {refus ? (
        <>
          <Input className="h-8 w-40" placeholder="Motif du refus" value={motif} onChange={(e) => setMotif(e.target.value)} />
          <Button size="sm" variant="destructive" disabled={!motif.trim() || actions.deciderPaiement.isPending} onClick={() => decider('REFUSER')}>
            Confirmer
          </Button>
        </>
      ) : (
        <Button size="sm" variant="ghost" onClick={() => setRefus(true)}>
          <XCircle className="size-4" /> Refuser
        </Button>
      )}
    </span>
  );
}

function ReponseCorrection({ agentId, correctionId }: { agentId: string; correctionId: string }) {
  const actions = useActionsHrms();
  const [reponse, setReponse] = useState('');
  const repondre = async (decision: 'ACCEPTER' | 'REFUSER') => {
    try {
      await actions.repondreCorrection.mutateAsync({ id: agentId, correctionId, corps: { decision, reponse: reponse || undefined } });
      toast.success(decision === 'ACCEPTER' ? 'Demande acceptée : portez la correction dans la section concernée.' : 'Demande refusée.');
    } catch (e) {
      erreur(e, 'Réponse impossible.');
    }
  };
  return (
    <span className="inline-flex flex-wrap items-center justify-end gap-1">
      <Input className="h-8 w-44" placeholder="Réponse à l’agent" value={reponse} onChange={(e) => setReponse(e.target.value)} />
      <Button size="sm" variant="outline" disabled={actions.repondreCorrection.isPending} onClick={() => repondre('ACCEPTER')}>
        Accepter
      </Button>
      <Button size="sm" variant="ghost" disabled={!reponse.trim() || actions.repondreCorrection.isPending} onClick={() => repondre('REFUSER')}>
        Refuser
      </Button>
    </span>
  );
}

// ------------------------------------------------------------------ dialogues de saisie
function Dialogue({ titre, description, enfants, onClose, onValider, enCours, libelle = 'Enregistrer', testid }: { titre: string; description?: string; enfants: ReactNode; onClose: () => void; onValider: () => void; enCours: boolean; libelle?: string; testid?: string }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl" data-testid={testid}>
        <DialogHeader>
          <DialogTitle>{titre}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">{enfants}</div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button onClick={onValider} disabled={enCours}>
            {libelle}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IdentiteDialog({ d, onClose }: { d: DossierHrms; onClose: () => void }) {
  const actions = useActionsHrms();
  const i = d.dossier.identite;
  const [s, setS] = useState({ nom: i.nom, prenom: i.prenom, email: i.email, sexe: (i.sexe ?? '') as 'F' | 'M' | '', lieuNaissance: i.lieuNaissance ?? '', dateNaissance: i.dateNaissance ?? '', statutMarital: (i.statutMarital ?? '') as StatutMaritalHrms | '', telephone: i.telephone ?? '', adresse: i.adresse ?? '', lieuRecrutement: i.lieuRecrutement ?? '', region: i.region ?? '', pieces: i.piecesAdministratives.map((p) => ({ type: p.type, numero: p.numero, validite: p.validite ?? '' })) });
  const champ = <K extends keyof typeof s>(k: K, v: (typeof s)[K]) => setS((x) => ({ ...x, [k]: v }));
  const valider = async () => {
    try {
      await actions.identite.mutateAsync({ id: d.fiche.id, corps: { nom: s.nom, prenom: s.prenom, email: s.email, sexe: s.sexe, lieuNaissance: s.lieuNaissance || null, dateNaissance: s.dateNaissance || null, statutMarital: s.statutMarital, telephone: s.telephone || null, adresse: s.adresse || null, lieuRecrutement: s.lieuRecrutement || null, region: s.region || null, piecesAdministratives: s.pieces.filter((p) => p.type.trim() && p.numero.trim()).map((p) => ({ type: p.type, numero: p.numero, validite: p.validite || null })) } });
      toast.success('Identité enregistrée.');
      onClose();
    } catch (e) {
      erreur(e, 'Enregistrement impossible.');
    }
  };
  return (
    <Dialogue titre="Identité et coordonnées" description="Changements journalisés ; l'adresse de courriel sert à la connexion et aux notifications." onClose={onClose} onValider={valider} enCours={actions.identite.isPending} testid="dialogue-identite" enfants={
      <>
        <div className="space-y-1"><Label htmlFor="id-nom">Nom et post-nom</Label><Input id="id-nom" value={s.nom} onChange={(e) => champ('nom', e.target.value)} /></div>
        <div className="space-y-1"><Label htmlFor="id-prenom">Prénom</Label><Input id="id-prenom" value={s.prenom} onChange={(e) => champ('prenom', e.target.value)} /></div>
        <div className="space-y-1"><Label htmlFor="id-sexe">Sexe</Label><SelectNatif id="id-sexe" value={s.sexe} onChange={(e) => champ('sexe', e.target.value as 'F' | 'M' | '')}><option value="">—</option><option value="F">Féminin</option><option value="M">Masculin</option></SelectNatif></div>
        <div className="space-y-1"><Label htmlFor="id-naissance">Date de naissance</Label><Input id="id-naissance" type="date" value={s.dateNaissance} onChange={(e) => champ('dateNaissance', e.target.value)} /></div>
        <div className="space-y-1"><Label htmlFor="id-lieu">Lieu de naissance</Label><Input id="id-lieu" value={s.lieuNaissance} onChange={(e) => champ('lieuNaissance', e.target.value)} /></div>
        <div className="space-y-1"><Label htmlFor="id-marital">Statut marital</Label><SelectNatif id="id-marital" value={s.statutMarital} onChange={(e) => champ('statutMarital', e.target.value as StatutMaritalHrms | '')}><option value="">—</option>{(Object.keys(LIBELLE_STATUT_MARITAL) as StatutMaritalHrms[]).map((k) => <option key={k} value={k}>{LIBELLE_STATUT_MARITAL[k]}</option>)}</SelectNatif></div>
        <div className="space-y-1"><Label htmlFor="id-tel">Téléphone</Label><Input id="id-tel" value={s.telephone} onChange={(e) => champ('telephone', e.target.value)} /></div>
        <div className="space-y-1"><Label htmlFor="id-email">Courriel</Label><Input id="id-email" type="email" value={s.email} onChange={(e) => champ('email', e.target.value)} /></div>
        <div className="space-y-1 sm:col-span-2"><Label htmlFor="id-adresse">Adresse</Label><Input id="id-adresse" value={s.adresse} onChange={(e) => champ('adresse', e.target.value)} /></div>
        <div className="space-y-1"><Label htmlFor="id-recrutement">Lieu de recrutement</Label><Input id="id-recrutement" value={s.lieuRecrutement} onChange={(e) => champ('lieuRecrutement', e.target.value)} /></div>
        <div className="space-y-1"><Label htmlFor="id-region">Région (KINSHASA = siège)</Label><Input id="id-region" value={s.region} onChange={(e) => champ('region', e.target.value)} /></div>
        <div className="space-y-2 sm:col-span-2">
          <Label>Pièces administratives</Label>
          {s.pieces.map((p, idx) => (
            <div key={idx} className="grid grid-cols-3 gap-2">
              <Input placeholder="Type (carte d’électeur…)" value={p.type} onChange={(e) => champ('pieces', s.pieces.map((x, i) => (i === idx ? { ...x, type: e.target.value } : x)))} />
              <Input placeholder="Numéro" value={p.numero} onChange={(e) => champ('pieces', s.pieces.map((x, i) => (i === idx ? { ...x, numero: e.target.value } : x)))} />
              <Input type="date" value={p.validite} onChange={(e) => champ('pieces', s.pieces.map((x, i) => (i === idx ? { ...x, validite: e.target.value } : x)))} />
            </div>
          ))}
          <Button type="button" size="sm" variant="outline" onClick={() => champ('pieces', [...s.pieces, { type: '', numero: '', validite: '' }])}>
            <Plus className="size-4" /> Ajouter une pièce
          </Button>
        </div>
      </>
    } />
  );
}

function AffectationDialog({ d, onClose }: { d: DossierHrms; onClose: () => void }) {
  const actions = useActionsHrms();
  const structures = useStructures();
  const agents = useAgentsRh({});
  const f = d.fiche;
  const [s, setS] = useState({ poste: f.fonction, grade: f.grade ?? '', categorie: f.categorie, structureCode: f.structure?.code ?? '', lieuPrestation: d.dossier.identite.lieuPrestation ?? '', superieurId: f.superieur?.id ?? '', motif: 'CHANGEMENT' as MotifAffectationHrms, effetDu: new Date().toISOString().slice(0, 10), commentaire: '' });
  const champ = <K extends keyof typeof s>(k: K, v: (typeof s)[K]) => setS((x) => ({ ...x, [k]: v }));
  const valider = async () => {
    try {
      await actions.affectation.mutateAsync({ id: f.id, corps: { poste: s.poste, grade: s.grade || null, categorie: s.categorie, structureCode: s.structureCode, lieuPrestation: s.lieuPrestation || null, superieurId: s.superieurId || null, motif: s.motif, effetDu: s.effetDu, commentaire: s.commentaire || null } });
      toast.success('Affectation enregistrée.');
      onClose();
    } catch (e) {
      erreur(e, 'Enregistrement impossible.');
    }
  };
  return (
    <Dialogue titre="Nouvelle affectation" description="Clôt l'affectation en cours la veille de la date d'effet et met à jour fonction, grade, catégorie, structure et supérieur hiérarchique." onClose={onClose} onValider={valider} enCours={actions.affectation.isPending} testid="dialogue-affectation" enfants={
      <>
        <div className="space-y-1 sm:col-span-2"><Label htmlFor="af-poste">Poste *</Label><Input id="af-poste" value={s.poste} onChange={(e) => champ('poste', e.target.value)} /></div>
        <div className="space-y-1"><Label htmlFor="af-grade">Grade</Label><Input id="af-grade" value={s.grade} onChange={(e) => champ('grade', e.target.value)} /></div>
        <div className="space-y-1"><Label htmlFor="af-cat">Catégorie *</Label><SelectNatif id="af-cat" value={s.categorie} onChange={(e) => champ('categorie', e.target.value as CategorieAgent)}>{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</SelectNatif></div>
        <div className="space-y-1"><Label htmlFor="af-structure">Structure *</Label><SelectNatif id="af-structure" value={s.structureCode} onChange={(e) => champ('structureCode', e.target.value)}><option value="">—</option>{(structures.data ?? []).map((x) => <option key={x.code} value={x.code}>{x.code} · {x.libelle}</option>)}</SelectNatif></div>
        <div className="space-y-1"><Label htmlFor="af-lieu">Lieu de prestation</Label><Input id="af-lieu" value={s.lieuPrestation} onChange={(e) => champ('lieuPrestation', e.target.value)} /></div>
        <div className="space-y-1 sm:col-span-2"><Label htmlFor="af-sup">Supérieur hiérarchique</Label><SelectNatif id="af-sup" value={s.superieurId} onChange={(e) => champ('superieurId', e.target.value)}><option value="">Aucun (mandataire, directeur)</option>{(agents.data ?? []).filter((a) => a.id !== f.id).map((a) => <option key={a.id} value={a.id}>{a.prenom} {a.nom} · {a.fonction}</option>)}</SelectNatif></div>
        <div className="space-y-1"><Label htmlFor="af-motif">Motif *</Label><SelectNatif id="af-motif" value={s.motif} onChange={(e) => champ('motif', e.target.value as MotifAffectationHrms)}>{(Object.keys(LIBELLE_MOTIF_AFFECTATION) as MotifAffectationHrms[]).filter((m) => m !== 'IMPORT').map((m) => <option key={m} value={m}>{LIBELLE_MOTIF_AFFECTATION[m]}</option>)}</SelectNatif></div>
        <div className="space-y-1"><Label htmlFor="af-effet">Date d'effet *</Label><Input id="af-effet" type="date" value={s.effetDu} onChange={(e) => champ('effetDu', e.target.value)} /></div>
        <div className="space-y-1 sm:col-span-2"><Label htmlFor="af-com">Commentaire</Label><Input id="af-com" value={s.commentaire} onChange={(e) => champ('commentaire', e.target.value)} /></div>
      </>
    } />
  );
}

function ContratDialog({ d, onClose }: { d: DossierHrms; onClose: () => void }) {
  const actions = useActionsHrms();
  const [s, setS] = useState({ type: 'CDI' as TypeContratHrms, evenement: (d.contrats.length ? 'AVENANT' : 'ENTREE') as EvenementContratHrms, debut: new Date().toISOString().slice(0, 10), fin: '', reference: '', commentaire: '' });
  const champ = <K extends keyof typeof s>(k: K, v: (typeof s)[K]) => setS((x) => ({ ...x, [k]: v }));
  const valider = async () => {
    try {
      await actions.contrat.mutateAsync({ id: d.fiche.id, corps: { type: s.type, evenement: s.evenement, debut: s.debut, fin: s.fin || null, reference: s.reference || null, commentaire: s.commentaire || null } });
      toast.success('Événement contractuel enregistré.');
      onClose();
    } catch (e) {
      erreur(e, 'Enregistrement impossible.');
    }
  };
  return (
    <Dialogue titre="Événement contractuel" description="Une entrée sans date d'engagement au référentiel la renseigne ; la fin d'un contrat se signale par l'événement « Fin »." onClose={onClose} onValider={valider} enCours={actions.contrat.isPending} testid="dialogue-contrat" enfants={
      <>
        <div className="space-y-1"><Label htmlFor="ct-type">Type *</Label><SelectNatif id="ct-type" value={s.type} onChange={(e) => champ('type', e.target.value as TypeContratHrms)}>{(Object.keys(LIBELLE_TYPE_CONTRAT) as TypeContratHrms[]).map((t) => <option key={t} value={t}>{LIBELLE_TYPE_CONTRAT[t]}</option>)}</SelectNatif></div>
        <div className="space-y-1"><Label htmlFor="ct-ev">Événement *</Label><SelectNatif id="ct-ev" value={s.evenement} onChange={(e) => champ('evenement', e.target.value as EvenementContratHrms)}>{(Object.keys(LIBELLE_EVENEMENT_CONTRAT) as EvenementContratHrms[]).map((t) => <option key={t} value={t}>{LIBELLE_EVENEMENT_CONTRAT[t]}</option>)}</SelectNatif></div>
        <div className="space-y-1"><Label htmlFor="ct-debut">Début *</Label><Input id="ct-debut" type="date" value={s.debut} onChange={(e) => champ('debut', e.target.value)} /></div>
        <div className="space-y-1"><Label htmlFor="ct-fin">Fin (vide : indéterminée)</Label><Input id="ct-fin" type="date" value={s.fin} onChange={(e) => champ('fin', e.target.value)} /></div>
        <div className="space-y-1"><Label htmlFor="ct-ref">Référence</Label><Input id="ct-ref" value={s.reference} onChange={(e) => champ('reference', e.target.value)} /></div>
        <div className="space-y-1"><Label htmlFor="ct-com">Commentaire</Label><Input id="ct-com" value={s.commentaire} onChange={(e) => champ('commentaire', e.target.value)} /></div>
      </>
    } />
  );
}

function AncienneteDialog({ d, onClose }: { d: DossierHrms; onClose: () => void }) {
  const actions = useActionsHrms();
  const [s, setS] = useState({ dateEngagement: d.fiche.dateEngagement ?? '', dateAnciennete: d.fiche.dateAnciennete ?? '', motif: '' });
  const valider = async () => {
    try {
      await actions.anciennete.mutateAsync({ id: d.fiche.id, corps: { dateEngagement: s.dateEngagement || null, dateAnciennete: s.dateAnciennete || null, motif: s.motif || null } });
      toast.success('Ancienneté enregistrée.');
      onClose();
    } catch (e) {
      erreur(e, 'Enregistrement impossible.');
    }
  };
  return (
    <Dialogue titre="Ancienneté" description="La date d'engagement ouvre les périodes de droits de congé ; la date d'ancienneté reconnue (reprise) majore le droit annuel." onClose={onClose} onValider={valider} enCours={actions.anciennete.isPending} enfants={
      <>
        <div className="space-y-1"><Label htmlFor="an-eng">Date d'engagement</Label><Input id="an-eng" type="date" value={s.dateEngagement} onChange={(e) => setS({ ...s, dateEngagement: e.target.value })} /></div>
        <div className="space-y-1"><Label htmlFor="an-anc">Ancienneté reconnue depuis</Label><Input id="an-anc" type="date" value={s.dateAnciennete} onChange={(e) => setS({ ...s, dateAnciennete: e.target.value })} /></div>
        <div className="space-y-1 sm:col-span-2"><Label htmlFor="an-motif">Motif (journalisé)</Label><Input id="an-motif" value={s.motif} onChange={(e) => setS({ ...s, motif: e.target.value })} /></div>
      </>
    } />
  );
}

function SocialDialog({ d, onClose }: { d: DossierHrms; onClose: () => void }) {
  const actions = useActionsHrms();
  const so = d.dossier.social;
  const [s, setS] = useState({ numeroCnss: so?.numeroCnss ?? '', numeroFiscal: so?.numeroFiscal ?? '', regimeSocial: so?.regimeSocial ?? '', dateAffiliationCnss: so?.dateAffiliationCnss ?? '' });
  const valider = async () => {
    try {
      await actions.social.mutateAsync({ id: d.fiche.id, corps: { numeroCnss: s.numeroCnss || null, numeroFiscal: s.numeroFiscal || null, regimeSocial: s.regimeSocial || null, dateAffiliationCnss: s.dateAffiliationCnss || null } });
      toast.success('Situation sociale enregistrée.');
      onClose();
    } catch (e) {
      erreur(e, 'Enregistrement impossible.');
    }
  };
  return (
    <Dialogue titre="Situation fiscale et sociale" onClose={onClose} onValider={valider} enCours={actions.social.isPending} enfants={
      <>
        <div className="space-y-1"><Label htmlFor="so-cnss">Numéro CNSS</Label><Input id="so-cnss" value={s.numeroCnss} onChange={(e) => setS({ ...s, numeroCnss: e.target.value })} /></div>
        <div className="space-y-1"><Label htmlFor="so-nif">Numéro fiscal</Label><Input id="so-nif" value={s.numeroFiscal} onChange={(e) => setS({ ...s, numeroFiscal: e.target.value })} /></div>
        <div className="space-y-1"><Label htmlFor="so-regime">Régime social</Label><Input id="so-regime" value={s.regimeSocial} onChange={(e) => setS({ ...s, regimeSocial: e.target.value })} /></div>
        <div className="space-y-1"><Label htmlFor="so-date">Date d'affiliation CNSS</Label><Input id="so-date" type="date" value={s.dateAffiliationCnss} onChange={(e) => setS({ ...s, dateAffiliationCnss: e.target.value })} /></div>
      </>
    } />
  );
}

function TempsDialog({ d, onClose }: { d: DossierHrms; onClose: () => void }) {
  const actions = useActionsHrms();
  const t = d.dossier.tempsTravail;
  const [s, setS] = useState({ horaireCode: t.horaireCode ?? '', calendrierCode: t.calendrierCode ?? '', modePointage: (t.modePointage ?? '') as 'BADGE' | 'FICHE' | '', eligibleHeuresSup: t.eligibleHeuresSup, eligibleAstreinte: t.eligibleAstreinte });
  const valider = async () => {
    try {
      await actions.tempsTravail.mutateAsync({ id: d.fiche.id, corps: { horaireCode: s.horaireCode || null, calendrierCode: s.calendrierCode || null, modePointage: s.modePointage, eligibleHeuresSup: s.eligibleHeuresSup, eligibleAstreinte: s.eligibleAstreinte } });
      toast.success('Temps de travail enregistré.');
      onClose();
    } catch (e) {
      erreur(e, 'Enregistrement impossible.');
    }
  };
  return (
    <Dialogue titre="Temps de travail" onClose={onClose} onValider={valider} enCours={actions.tempsTravail.isPending} enfants={
      <>
        <div className="space-y-1"><Label htmlFor="tt-horaire">Code horaire</Label><Input id="tt-horaire" value={s.horaireCode} placeholder="STANDARD" onChange={(e) => setS({ ...s, horaireCode: e.target.value })} /></div>
        <div className="space-y-1"><Label htmlFor="tt-cal">Code calendrier</Label><Input id="tt-cal" value={s.calendrierCode} placeholder="SIEGE" onChange={(e) => setS({ ...s, calendrierCode: e.target.value })} /></div>
        <div className="space-y-1"><Label htmlFor="tt-pointage">Mode de pointage</Label><SelectNatif id="tt-pointage" value={s.modePointage} onChange={(e) => setS({ ...s, modePointage: e.target.value as 'BADGE' | 'FICHE' | '' })}><option value="">—</option><option value="BADGE">Badge</option><option value="FICHE">Fiche de présence</option></SelectNatif></div>
        <div className="flex flex-col justify-end gap-2">
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={s.eligibleHeuresSup} onCheckedChange={(v) => setS({ ...s, eligibleHeuresSup: v === true })} /> Éligible aux heures supplémentaires</label>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={s.eligibleAstreinte} onCheckedChange={(v) => setS({ ...s, eligibleAstreinte: v === true })} /> Éligible à l'astreinte</label>
        </div>
      </>
    } />
  );
}

function ChargeDialog({ d, onClose, onPieces }: { d: DossierHrms; onClose: () => void; onPieces: () => void }) {
  const actions = useActionsHrms();
  const [s, setS] = useState({ lien: 'ENFANT' as LienChargeHrms, nom: d.fiche.nom, prenom: '', dateNaissance: '', pieceId: '' });
  const valider = async () => {
    try {
      await actions.declarerCharge.mutateAsync({ id: d.fiche.id, corps: { lien: s.lien, nom: s.nom, prenom: s.prenom || null, dateNaissance: s.dateNaissance || null, pieceId: s.pieceId || null } });
      toast.success('Personne à charge déclarée : les Ressources humaines la valident sur justificatif.');
      onClose();
    } catch (e) {
      erreur(e, 'Déclaration impossible.');
    }
  };
  return (
    <Dialogue titre="Déclarer une personne à charge" description="Joignez d'abord le justificatif (acte de mariage, acte de naissance) dans l'onglet « Pièces », puis indiquez son identifiant ici ; sans pièce, la validation est impossible (décision D22)." onClose={onClose} onValider={valider} enCours={actions.declarerCharge.isPending} libelle="Déclarer" testid="dialogue-charge" enfants={
      <>
        <div className="space-y-1"><Label htmlFor="ch-lien">Lien *</Label><SelectNatif id="ch-lien" value={s.lien} onChange={(e) => setS({ ...s, lien: e.target.value as LienChargeHrms })}>{(Object.keys(LIBELLE_LIEN_CHARGE) as LienChargeHrms[]).map((l) => <option key={l} value={l}>{LIBELLE_LIEN_CHARGE[l]}</option>)}</SelectNatif></div>
        <div className="space-y-1"><Label htmlFor="ch-naissance">Date de naissance</Label><Input id="ch-naissance" type="date" value={s.dateNaissance} onChange={(e) => setS({ ...s, dateNaissance: e.target.value })} /></div>
        <div className="space-y-1"><Label htmlFor="ch-nom">Nom *</Label><Input id="ch-nom" value={s.nom} onChange={(e) => setS({ ...s, nom: e.target.value })} /></div>
        <div className="space-y-1"><Label htmlFor="ch-prenom">Prénom</Label><Input id="ch-prenom" value={s.prenom} onChange={(e) => setS({ ...s, prenom: e.target.value })} /></div>
        <div className="space-y-1 sm:col-span-2">
          <Label htmlFor="ch-piece">Identifiant de la pièce justificative</Label>
          <Input id="ch-piece" value={s.pieceId} placeholder="collé depuis l'onglet « Pièces »" onChange={(e) => setS({ ...s, pieceId: e.target.value })} />
          <Button type="button" variant="link" size="sm" className="px-0" onClick={onPieces}>
            Ouvrir l'onglet « Pièces » pour déposer le justificatif
          </Button>
        </div>
      </>
    } />
  );
}

function PaiementDialog({ d, onClose }: { d: DossierHrms; onClose: () => void }) {
  const actions = useActionsHrms();
  const [s, setS] = useState({ mode: 'VIREMENT' as ModePaiementHrms, banque: '', compte: '', devise: 'USD' as 'USD' | 'CDF', effetDu: '' });
  const valider = async () => {
    try {
      await actions.proposerPaiement.mutateAsync({ id: d.fiche.id, corps: { mode: s.mode, banque: s.banque || null, compte: s.compte || null, devise: s.devise, effetDu: s.effetDu || null } });
      toast.success('Coordonnées proposées : le Directeur RH est notifié pour validation.');
      onClose();
    } catch (e) {
      erreur(e, 'Enregistrement impossible.');
    }
  };
  return (
    <Dialogue titre="Nouvelles coordonnées de paiement" description="Double contrôle : la saisie est soumise à la validation du Directeur RH, qui ne peut pas être la personne qui saisit." onClose={onClose} onValider={valider} enCours={actions.proposerPaiement.isPending} libelle="Proposer" testid="dialogue-paiement" enfants={
      <>
        <div className="space-y-1"><Label htmlFor="pa-mode">Mode *</Label><SelectNatif id="pa-mode" value={s.mode} onChange={(e) => setS({ ...s, mode: e.target.value as ModePaiementHrms })}>{(Object.keys(LIBELLE_MODE_PAIEMENT_HRMS) as ModePaiementHrms[]).map((m) => <option key={m} value={m}>{LIBELLE_MODE_PAIEMENT_HRMS[m]}</option>)}</SelectNatif></div>
        <div className="space-y-1"><Label htmlFor="pa-devise">Devise</Label><SelectNatif id="pa-devise" value={s.devise} onChange={(e) => setS({ ...s, devise: e.target.value as 'USD' | 'CDF' })}><option value="USD">USD</option><option value="CDF">CDF</option></SelectNatif></div>
        <div className="space-y-1"><Label htmlFor="pa-banque">Banque ou opérateur</Label><Input id="pa-banque" value={s.banque} onChange={(e) => setS({ ...s, banque: e.target.value })} /></div>
        <div className="space-y-1"><Label htmlFor="pa-compte">Numéro de compte</Label><Input id="pa-compte" value={s.compte} onChange={(e) => setS({ ...s, compte: e.target.value })} /></div>
        <div className="space-y-1"><Label htmlFor="pa-effet">Date d'effet</Label><Input id="pa-effet" type="date" value={s.effetDu} onChange={(e) => setS({ ...s, effetDu: e.target.value })} /></div>
      </>
    } />
  );
}

function CorrectionDialog({ d, onClose }: { d: DossierHrms; onClose: () => void }) {
  const actions = useActionsHrms();
  const [s, setS] = useState({ section: 'IDENTITE' as SectionDossierHrms, champ: '', valeurActuelle: '', valeurProposee: '', motif: '' });
  const valider = async () => {
    try {
      await actions.demanderCorrection.mutateAsync({ id: d.fiche.id, corps: { section: s.section, champ: s.champ, valeurActuelle: s.valeurActuelle || null, valeurProposee: s.valeurProposee, motif: s.motif || null } });
      toast.success('Demande transmise aux Ressources humaines.');
      onClose();
    } catch (e) {
      erreur(e, 'Demande impossible.');
    }
  };
  return (
    <Dialogue titre="Demander une correction" description="Indiquez l'information erronée et la valeur exacte ; joignez au besoin un justificatif dans l'onglet « Pièces »." onClose={onClose} onValider={valider} enCours={actions.demanderCorrection.isPending} libelle="Envoyer" testid="dialogue-correction" enfants={
      <>
        <div className="space-y-1"><Label htmlFor="co-section">Section *</Label><SelectNatif id="co-section" value={s.section} onChange={(e) => setS({ ...s, section: e.target.value as SectionDossierHrms })}>{(Object.keys(LIBELLE_SECTION_DOSSIER) as SectionDossierHrms[]).map((k) => <option key={k} value={k}>{LIBELLE_SECTION_DOSSIER[k]}</option>)}</SelectNatif></div>
        <div className="space-y-1"><Label htmlFor="co-champ">Information *</Label><Input id="co-champ" value={s.champ} placeholder="téléphone, adresse, date de naissance…" onChange={(e) => setS({ ...s, champ: e.target.value })} /></div>
        <div className="space-y-1"><Label htmlFor="co-actuelle">Valeur actuelle</Label><Input id="co-actuelle" value={s.valeurActuelle} onChange={(e) => setS({ ...s, valeurActuelle: e.target.value })} /></div>
        <div className="space-y-1"><Label htmlFor="co-proposee">Valeur exacte *</Label><Input id="co-proposee" value={s.valeurProposee} onChange={(e) => setS({ ...s, valeurProposee: e.target.value })} /></div>
        <div className="space-y-1 sm:col-span-2"><Label htmlFor="co-motif">Explication</Label><Textarea id="co-motif" value={s.motif} onChange={(e) => setS({ ...s, motif: e.target.value })} /></div>
      </>
    } />
  );
}

function StatutDialog({ d, onClose }: { d: DossierHrms; onClose: () => void }) {
  const actions = useActionsHrms();
  const [statut, setStatut] = useState<StatutDossierHrms>(d.dossier.statut === 'CONTROLE' && d.droits.valider ? 'VALIDE' : 'CONTROLE');
  const [commentaire, setCommentaire] = useState('');
  const valider = async () => {
    try {
      await actions.statut.mutateAsync({ id: d.fiche.id, statut, commentaire: commentaire || undefined });
      toast.success(`Dossier ${LIBELLE_STATUT_DOSSIER_HRMS[statut].toLowerCase()}.`);
      onClose();
    } catch (e) {
      erreur(e, 'Changement de statut impossible.');
    }
  };
  return (
    <Dialogue titre="Statut du dossier" description="Contrôlé par les RH après vérification des pièces ; validé par le Directeur RH. Aucune anomalie bloquante ne doit subsister." onClose={onClose} onValider={valider} enCours={actions.statut.isPending} testid="dialogue-statut" enfants={
      <>
        <div className="space-y-1"><Label htmlFor="st-statut">Nouveau statut</Label><SelectNatif id="st-statut" value={statut} onChange={(e) => setStatut(e.target.value as StatutDossierHrms)}><option value="IMPORTE">Importé</option><option value="A_REGULARISER">À régulariser</option><option value="CONTROLE">Contrôlé</option>{d.droits.valider && <option value="VALIDE">Validé</option>}</SelectNatif></div>
        <div className="space-y-1"><Label htmlFor="st-com">Commentaire</Label><Input id="st-com" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} /></div>
      </>
    } />
  );
}
